import isError from 'lodash/isError';
import { invariant } from '../util/invariant';
import type { PixelFormat, Resolution, Format, FieldOrder, ScaleMode, ImageFormat, MpegTs } from './types';
import { filenameExtensionForFormat, mimeTypeForFormat } from './types';
import type { AudioCodec, VideoCodec } from './codecs';
import { FRAC_ONE, type Fraction } from '../util/fractionType';
import { frac } from '../util/integerMath';
import { makeValidationFunction } from '../util/validation';
import type { ResolutionInfo } from './graphInfoTypes';
import type { ColorSpace } from '../types/colorSpace';
import type { CompleteType } from '../util/general';

export interface ResolutionLimits {
  maxWidth?: number,
  maxHeight?: number,
}


/**
 *  At some point our color space was quite minimal and the OutputProfiles
 *  stored in various places in our database still have this legacy color
 *  space information
 */
export type LegacyColorSpaceSpace = 'bt470m' | 'bt470bg' | 'bt601-6-525' | 'bt601-6-625' | 'bt709' | 'smpte170m' | 'smpte240m' | 'bt2020' | 'srgb';
export type LegacyColorSpaceRange = 'full' | 'limited';

export interface LegacyColorSpace {
  /**
   *  This is a setting that affects space, transfer characteristics and
   *  primaries which are all part of the definition of a color space
   */
  space: LegacyColorSpaceSpace,

  /**
   *  Color range is not part of the color space definitions as this maps how
   *  to convert the samples of a pixel format to the normalized input values
   *  to the color space calculations.
   */
  range: LegacyColorSpaceRange,
}

export type ColorSpaceCompat = ColorSpace | LegacyColorSpace;

export function resolveColorSpaceCompat(colorSpace: ColorSpaceCompat | undefined): ColorSpace | undefined {
  if (colorSpace === undefined) {
    return undefined;
  }
  if ('matrix' in colorSpace) {
    return colorSpace;
  }

  switch (colorSpace.space) {
    case 'bt709':
      return {
        matrix: {
          type: 'BT.709',
          range: colorSpace.range,
        },
        primaries: 'BT.709',
        transfer: 'BT.709',
      };
    case 'bt2020':
      return {
        matrix: {
          type: 'BT.2020',
          range: colorSpace.range,
        },
        primaries: 'BT.2020',
        // BT.2020 SDR uses the same transfer function as BT.709
        transfer: 'BT.709',
      };
    case 'srgb':
      return {
        matrix: { type: 'RGB' },
        primaries: 'BT.709',
        transfer: 'sRGB',
      };
    default:
      // We only store color space information in the database for profiles
      // we've defined. We never used anything but BT.709 as far as I know.
      throw new Error(`Unsupported color space in legacy color space: ${colorSpace.space}`);
  }
}

export function resolutionConformingToLimits(
  resolution: ResolutionInfo | 'unknown',
  limits?: ResolutionLimits,
) {
  if (resolution === 'unknown') {
    // We can't know
    return false;
  }
  if (limits === undefined) {
    // There are no limits to conform to
    return true;
  }
  if ((limits.maxHeight !== undefined) && (resolution.height > limits.maxHeight)) {
    return false;
  }
  if ((limits.maxWidth !== undefined) && (resolution.width > limits.maxWidth)) {
    return false;
  }
  return true;
}

export interface VideoOutputProfile {
  codec: VideoCodec,
  frameRate?: Fraction,
  resolution?: Resolution,
  // Height limit is applied after resolution if both are specified
  resolutionLimits?: ResolutionLimits,
  pixelFormat?: PixelFormat,
  colorSpace?: ColorSpace,
  scaleMode?: ScaleMode,
  fieldOrder?: FieldOrder,
}

export interface VideoOutputProfileCompat {
  codec: VideoCodec,
  frameRate?: Fraction,
  resolution?: Resolution,
  resolutionLimits?: ResolutionLimits,
  pixelFormat?: PixelFormat,
  colorSpace?: ColorSpaceCompat,
  scaleMode?: ScaleMode,
  fieldOrder?: FieldOrder,
}

export function resolveVideoOutputProfileCompat(videoOutputProfile: VideoOutputProfileCompat | undefined): VideoOutputProfile | undefined {
  if (videoOutputProfile === undefined) {
    return videoOutputProfile;
  }
  const resolved : CompleteType<VideoOutputProfile> = {
    codec: videoOutputProfile.codec,
    frameRate: videoOutputProfile.frameRate,
    resolution: videoOutputProfile.resolution,
    resolutionLimits: videoOutputProfile.resolutionLimits,
    pixelFormat: videoOutputProfile.pixelFormat,
    colorSpace: resolveColorSpaceCompat(videoOutputProfile.colorSpace),
    scaleMode: videoOutputProfile.scaleMode,
    fieldOrder: videoOutputProfile.fieldOrder,
  };
  return resolved;
}

export interface AudioChannelLimits {
  min?: number,
  max?: number,
  // If default mix is set to true then the adjustments to the channel
  // count is done by doing a default mix-down or mix-up. If this is
  // set to false or undefined then channels will simply be ignored
  // if going over the limit and if the number of input channels is
  // lower than the min limit silent channels will be added.
  defaultMix?: boolean,
}

export interface AudioOutputProfile {
  codec: AudioCodec,
  sampleRate?: number,
  // This settings determines how many channels there should be per stream.
  // Combined with this value the number of input channels will therefore
  // determine the number of output audio streams. If the input channel
  // count is 7 and this value is set to 2 then there will be 4 output
  // streams with the first 3 having 2 channels and the last one having 1.
  // If omitted, all channels will go into one stream.
  audioChannelsPerStream?: number,
  channelLimits?: AudioChannelLimits,

  // Set this to control the number of audio samples per channel per chunk.
  // Only relevant for uncompressed audio as most audio codecs have a fixed
  // audio frame size.
  audioFrameSize?: number,
}

export type ImageFormatName = ImageFormat['name'];

export type FormatName = Format['name'];

export interface SegmentedContainerProfile {
  type: 'segmented',
  // In order to preserve backwards compatibility we need to support only
  // specifying the name of the format
  format: MpegTs | 'mpegts',
  // Segment duration in seconds
  segmentDuration: number,
}

export interface SingleFileContainerProfile {
  type: 'single_file',
  // In order to preserve backwards compatibility we need to support only
  // specifying the name of the format
  format: Format | FormatName,
}

export interface ImagesContainerProfile {
  type: 'images',
  // In order to preserve backwards compatibility we need to support only
  // specifying the name of the format
  format: ImageFormat | ImageFormatName,
}

export function resolveFormat(format: Format | FormatName): Format {
  switch (format) {
    case 'mp4':
    case 'mov':
    case 'm4a':
      return { name: format, faststart: false };
    case 'mpegts':
    case 'webm':
    case 'wav':
    case 'mxf':
    case 'nut':
    case 'mpjpeg':
    case 'raw':
    case 'raw_pcm16':
      return { name: format };
    case 'fifo':
      throw new Error('Shorthand form of fifo makes no sense since there is no information about the inner format');
    default:
      return format;
  }
}

export function resolveSegmentedFormat(format: MpegTs | 'mpegts'): MpegTs {
  switch (format) {
    case 'mpegts':
      return { name: format };
    default:
      return format;
  }
}

export function outputProfileIsMxf(outputProfile: OutputProfile): boolean {
  if (outputProfile.container.type !== 'single_file') {
    return false;
  }

  return resolveFormat(outputProfile.container.format).name === 'mxf';
}

export function resolveImageFormat(format: ImageFormat | ImageFormatName): ImageFormat {
  switch (format) {
    case 'jpg':
    case 'png':
      return { name: format };
    default:
      return format;
  }
}

export interface RTMPContainer {
  type: 'rtmp',
}

export interface RTMPContainer {
  type: 'rtmp',
}

export type ContainerProfile =
  SegmentedContainerProfile |
  SingleFileContainerProfile |
  ImagesContainerProfile |
  RTMPContainer;

export function extensionForContainerProfile(container: ContainerProfile): string {
  switch (container.type) {
    case 'single_file':
      return filenameExtensionForFormat(resolveFormat(container.format));
    case 'images':
      return filenameExtensionForFormat(resolveImageFormat(container.format));
    case 'rtmp':
      return '.flv';
    case 'segmented':
      return filenameExtensionForFormat(resolveSegmentedFormat(container.format));
  }
}

export function mimeTypeForContainerProfile(container: ContainerProfile): string {
  switch (container.type) {
    case 'single_file':
      return mimeTypeForFormat(resolveFormat(container.format));
    case 'images':
      return mimeTypeForFormat(resolveImageFormat(container.format));
    case 'rtmp':
      return 'video/x-flv';
    case 'segmented':
      return mimeTypeForFormat(resolveSegmentedFormat(container.format));
  }
}

export interface OutputProfile {
  container: ContainerProfile,
  video?: VideoOutputProfile,
  audio?: AudioOutputProfile,
}

export interface OutputProfileCompat {
  container: ContainerProfile,
  video?: VideoOutputProfileCompat,
  audio?: AudioOutputProfile,
}

export function resolveOutputProfileCompat(outputProfile: OutputProfileCompat): OutputProfile {
  const resolve : CompleteType<OutputProfile> = {
    container: outputProfile.container,
    video: resolveVideoOutputProfileCompat(outputProfile.video),
    audio: outputProfile.audio,
  };
  return resolve;
}

export const DEFAULT_S3_SEGMENT_DURATION = 2;

export const DEFAULT_VIDEO_OUTPUT_PROFILE: VideoOutputProfile = {
  codec: {
    codec: 'h264',
    rateControl: {
      type: 'crf',
      crf: 18,
    },
    bFrames: 0,
    preset: 'veryfast',
    profile: 'main',
  }
};

export const DEFAULT_S3_HIGHRES_OUTPUT_PROFILE: OutputProfile = {
  container: {
    type: 'segmented',
    format: { name: 'mpegts' },
    segmentDuration: DEFAULT_S3_SEGMENT_DURATION,
  },
  video: {
    codec: {
      codec: 'h264',
      rateControl: {
        type: 'crf',
        crf: 19,
      },
      bFrames: 0,
      preset: 'veryfast',
      profile: 'main',
    },
    pixelFormat: 'yuv420p',
  },
  audio: {
    codec: {
      codec: 'aac',
      bitrate: 192000,
    },
    sampleRate: 48000,
    audioChannelsPerStream: 2,
  }
};

export const DEFAULT_S3_WEBPROXY_OUTPUT_PROFILE: OutputProfile = {
  container: {
    type: 'segmented',
    format: { name: 'mpegts' },
    segmentDuration: DEFAULT_S3_SEGMENT_DURATION,
  },
  video: {
    codec: {
      codec: 'h264',
      rateControl: {
        type: 'crf',
        crf: 34,
      },
      bFrames: 0,
      preset: 'veryfast',
      profile: 'main',
    },
    pixelFormat: 'yuv420p',
    fieldOrder: 'progressive',
  },
  audio: {
    codec: {
      codec: 'aac',
      bitrate: 192000,
    },
    sampleRate: 48000,
    audioChannelsPerStream: 2
  }
};

export const DEFAULT_INTERMEDIATE_AWS_MEDIA_LIVE_OUTPUT_PROFILE : OutputProfile = {
  container: {
    type: 'single_file',
    format: {
      name: 'mpegts',
    },
  },
  video: {
    codec: {
      codec: 'h264',
      rateControl: {
        type: 'crf',
        crf: 17,
        maxBitrate: 5000000,
      },
      profile: 'baseline',
      gopSize: 48
    },
    pixelFormat: 'yuv420p',
  },
  audio: {
    codec: {
      codec: 'aac',
      bitrate: 192000,
    },
  }
};

export const DEFAULT_TS_LIVE_FOLDER_PROXY_PROFILE: OutputProfile = {
  container: {
    type: 'segmented',
    format: { name: 'mpegts' },
    segmentDuration: DEFAULT_S3_SEGMENT_DURATION,
  },
  video: {
    codec: {
      codec: 'h264',
      rateControl: {
        type: 'crf',
        crf: 25,
      },
      bFrames: 0,
      preset: 'veryfast',
      profile: 'main',
    },
    pixelFormat: 'yuv420p',
    fieldOrder: 'progressive',
  },
  audio: {
    codec: {
      codec: 'aac',
      bitrate: 192000,
    },
    sampleRate: 48000,
    audioChannelsPerStream: 2
  }
};

export const DEFAULT_TS_LIVE_FOLDER_AUDIO_PROFILE: OutputProfile = {
  container: {
    type: 'segmented',
    format: { name: 'mpegts' },
    segmentDuration: DEFAULT_S3_SEGMENT_DURATION,
  },
  audio: {
    codec: {
      codec: 'aac',
      bitrate: 192000,
    },
    sampleRate: 48000,
    audioChannelsPerStream: 2,
  }
};

export const DEFAULT_THUMBNAIL_PROFILE: OutputProfile = {
  container: {
    type: 'images',
    format: { name: 'jpg' },
  },
  video: {
    codec: {
      codec: 'mjpeg',
    },
    resolutionLimits: {
      maxWidth: 320,
      maxHeight: 180,
    },
    frameRate: FRAC_ONE,
  }
};

export const DEFAULT_MJPEG_LIVE_PREVIEW_PROFILE: OutputProfile = {
  container: {
    type: 'single_file',
    // We wrap mjpeg in the fifo muxer with recover on error
    // enabled so that the entire ingest is not stopped if the
    // s3 server is restarted
    format: {
      name: 'fifo',
      inner: {
        name: 'mpjpeg',
      },
      recoverOnError: 'all',
      queueSize: 5,
      dropOnOverflow: true,
    },
  },
  video: {
    codec: {
      codec: 'mjpeg',
      quality: 6,
    },
    resolution: {
      width: 320,
      height: 180,
      sar: FRAC_ONE,
    },
    fieldOrder: 'progressive',
  },
};

export const DEFAULT_LIVE_TRANSCRIPTION_PROFILE: OutputProfile = {
  container: {
    type: 'rtmp',
  },
  audio: {
    codec: {
      codec: 'aac',
      bitrate: 192000,
    },
    sampleRate: 48000,
    audioChannelsPerStream: 1,
    channelLimits: {
      max: 1,
      defaultMix: true,
    },
  }
};

export function parseVideoOutputProfile(json: string): VideoOutputProfile {
  const maybe = JSON.parse(json);
  invariant(maybe, 'VideoOutputProfile is not valid JSON');
  return ensureIsVideoOutputProfile(maybe);
}

export function parseOutputProfile(json: string): OutputProfile {
  return ensureIsOutputProfile(JSON.parse(json));
}

export function ensureIsOutputProfile(maybe: any): OutputProfile {
  if (maybe.video !== undefined) {
    maybe.video = ensureIsVideoOutputProfile(maybe.video);
  }

  // TODO: Improve audio and container sanity checks?
  invariant(maybe.container !== undefined, 'OutputProfile is missing "container" field');
  if (maybe.audio !== undefined) {
    maybe.audio = maybe.audio as AudioOutputProfile;
  }
  maybe.container = maybe.container as ContainerProfile;
  return maybe;
}

/**
 *  Warning: This is not a proper validation method. It makes a very shallow sanity check
 */
export const validateOutputProfile =
  makeValidationFunction((u) => {
    try {
      return ensureIsOutputProfile(u);
    } catch (error) {
      if (isError(error)) {
        throw error.message;
      } else {
        throw error;
      }
    }
  });

const optionalProfileProperties: ReadonlySet<string> = new Set(['resolution', 'pixelFormat', 'frameRate', 'fieldOrder', 'resolutionLimits', 'colorSpace', 'scaleMode']);

export function ensureIsVideoOutputProfile(maybe: any): VideoOutputProfile {
  const missing = ['codec'];
  const unexpected = [];
  for (const key of Object.keys(maybe)) {
    // Optional keys
    if (optionalProfileProperties.has(key)) {
      continue;
    }

    // Mandatory keys
    if (key === 'codec') {
      if ((maybe[key] !== undefined) && (maybe[key] !== null)) {
        missing.splice(missing.indexOf(key), 1);
        continue;
      }
    }

    unexpected.push(key);
  }
  invariant(missing.length === 0, `VideoOutputProfile is missing the following mandatory fields: ${missing.join(', ')}`);
  invariant(unexpected.length === 0, `Unexpected fields in VideoOutputProfile: ${unexpected.join(', ')}`);

  return maybe as VideoOutputProfile;
}

export function getFieldOrderFromProfile(outputProfile: OutputProfile): FieldOrder | undefined {
  return outputProfile.video?.fieldOrder;
}

/**
 * Returns the frame rate from the output profile's video settings.
 * Defaults to 25 fps if no video output profile or frame rate is specified.
 */
export function getFrameRateFromOutputProfile(videoOutputProfile: VideoOutputProfile | undefined): Fraction {
  return videoOutputProfile?.frameRate ?? frac(25, 1);
}

